/**
 * useContextBuilder.ts — State and submit logic for the Context Builder module.
 */
import { useCallback, useEffect, useState } from 'react'
import { context } from '@/api/client'
import type { ContextOptions, ContextPreset } from '@/types'
import { DEFAULT_CONTEXT_OPTIONS } from '@/types'
import { useJobPoller } from './useJobPoller'

export function useContextBuilder() {
  const [options,  setOptions]  = useState<ContextOptions>(DEFAULT_CONTEXT_OPTIONS)
  const [presets,  setPresets]  = useState<ContextPreset[]>([])
  const [presetsLoading, setPresetsLoading] = useState(true)

  const { jobView, beginJob, cancel, reset, setError } = useJobPoller({
    getJob:    context.getJob,
    cancelJob: context.cancelJob,
  })

  // Load presets on mount
  useEffect(() => {
    context.getPresets()
      .then(setPresets)
      .catch(() => {})
      .finally(() => setPresetsLoading(false))
  }, [])

  const submit = useCallback(async () => {
    try {
      const body: Record<string, unknown> = {}
      if (options.preset) {
        body.preset = options.preset
      } else if (options.config_yaml) {
        body.config_yaml = options.config_yaml
      } else {
        body.company    = options.company
        body.product    = options.product
        body.department = options.department
        body.challenges = options.challenges
      }
      const job = await context.createJob(body)
      await beginJob(job.job_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start context build.')
    }
  }, [options, beginJob, setError])

  return { options, setOptions, presets, presetsLoading, jobView, submit, cancel, reset }
}
